// PowerSync local-first dispatch for `completeSession` / `reopenSession`.
//
// Extracted from capture-client-powersync.ts (audit-1 #9) so the parent
// adapter stays under the 500-line gate, mirroring the other dispatch
// siblings (capture-client-powersync-create-session.ts, -create-item.ts,
// -cancel-session.ts, …). The parent's `dispatch` switch delegates the two
// session-state kinds here.
//
// Both kinds write the SAME row with the SAME columns; the only signal of
// intent is the prior value of completed_at (carried to the connector by the
// schema's `trackPrevious` option):
//
//   prev null     → now non-null    => connector PUTs /complete
//   prev non-null → now null        => connector PUTs /reopen
//   same completion state           => the adapter writes NOTHING (see the
//                                       `isActualTransition` gate below)
//
// THE SAME-STATE WEDGE THIS GATES (CITYCARE-ASSET-CAPTURE-1S, 2026-06-29). A
// redundant complete/reopen (completed_at non-null → non-null, or null → null)
// must not issue the UPDATE: doing so bumps sync_version and queues a same-state
// capture_sessions PATCH that `dispatchSessionPatch` cannot map to a /complete or
// /reopen endpoint. Before the connector's no-op ack (audit-1 #2 / Layer 2), that
// PATCH threw, uploadData rethrew, batch.complete never ran, and the WHOLE device
// upload queue wedged behind it. This adapter gate is Layer 1: it never emits the
// bad PATCH in the first place; the connector ack is the backstop for pre-fix
// devices that already queued one.

import type {
  CaptureMutation,
  CaptureMutationResult,
} from './capture-client.types'
import { eventForResult } from './capture-client-events'
import type { CaptureEventBus } from './capture-events'

import type { PowerSyncDb } from './capture-client-powersync-db'

export async function dispatchSessionStateChange(
  mutation: Extract<
    CaptureMutation,
    { kind: 'completeSession' | 'reopenSession' }
  >,
  db: PowerSyncDb,
  bus: CaptureEventBus,
): Promise<CaptureMutationResult> {
  // Read previous completed_at + building_id. `isActualTransition` gates BOTH
  // the UPDATE below AND the buildings mirror: a redundant complete/reopen
  // (same completion state) must write NOTHING — a same-state PATCH wedges the
  // whole upload queue (CITYCARE-ASSET-CAPTURE-1S; see connector-dispatch-
  // session.ts). Mirror gate = PR #537 Codex P2; UPDATE gate = the wedge fix.
  //
  // The ONLY producer of a same-state dispatch is the backend recovery-reopen
  // branch (apps/backend/src/capture/reopen-session.ts) flowing back through the
  // create/resume path — NOT the in-UI complete/reopen toggle, which the screens
  // only surface when the session is in the opposite state. (audit-1 #10 —
  // recovery-scenario origin pointer restored.)
  const prevRows = await db.getAll<{
    completedAt: string | null
    buildingId: string | null
  }>(
    `SELECT
       completed_at AS completedAt,
       building_id  AS buildingId
     FROM capture_sessions
     WHERE id = ?
     LIMIT 1`,
    [mutation.sessionId],
  )
  const prevCompletedAt = prevRows[0]?.completedAt ?? null
  const prevBuildingId = prevRows[0]?.buildingId ?? null
  const isActualTransition =
    mutation.kind === 'completeSession'
      ? prevCompletedAt === null
      : prevCompletedAt !== null

  if (!isActualTransition) {
    // NO-OP (same completion state). Return uniformly side-effect-free: NO
    // completedAt computation, NO UPDATE, NO readback SELECT, NO buildings
    // mirror, NO bus event — and crucially the UNCHANGED syncVersion (never
    // syncVersion + 1), so a consumer that read this result can't queue a
    // follow-up PATCH off a phantom bump (audit-1 #4 / #7). Build the envelope
    // from `prevCompletedAt` (already derived from prevRows[0], with a null
    // fallback when prevRows is empty — corrupt local state / race), so the
    // missing-row case still returns a minimal row carrying the input
    // syncVersion as-is rather than a +1 bump.
    const session = {
      id: mutation.sessionId,
      syncVersion: mutation.syncVersion,
      completedAt: prevCompletedAt,
    }
    return { kind: mutation.kind, session }
  }

  // completeSession: now → ISO string; reopenSession: null. Provisional — the
  // HP1 Postgres trigger overwrites updated_at + the authoritative
  // completed_at on upload.
  const completedAt =
    mutation.kind === 'completeSession' ? new Date().toISOString() : null

  await db.execute(
    `UPDATE capture_sessions
       SET completed_at = ?,
           sync_version = ?,
           updated_at = strftime('%Y-%m-%dT%H:%M:%fZ','now')
       WHERE id = ?`,
    [completedAt, mutation.syncVersion + 1, mutation.sessionId],
  )

  // Read the row back so consumers see the bumped sync_version. UI
  // consumers (site-/building-capture-screen) cast the result to
  // SessionSummary and only read `.syncVersion` — the row's flat
  // fields cover that. Building/site nested objects on the
  // SessionSummary are not present locally; consumers that need
  // them re-read via `useSites` / route loaders.
  //
  // We also need `buildingId` HERE (not just for the response) so the
  // optimistic buildings.status mirror below can target the right row
  // without an extra SELECT.
  const rows = await db.getAll<Record<string, unknown>>(
    `SELECT
       id                AS id,
       site_id           AS siteId,
       building_id       AS buildingId,
       user_id           AS userId,
       user_display_name AS userDisplayName,
       started_at        AS startedAt,
       completed_at      AS completedAt,
       source_device_id  AS sourceDeviceId,
       synced_at         AS syncedAt,
       sync_version      AS syncVersion,
       created_at        AS createdAt,
       updated_at        AS updatedAt
     FROM capture_sessions
     WHERE id = ?
     LIMIT 1`,
    [mutation.sessionId],
  )
  const row = rows[0] ?? {
    id: mutation.sessionId,
    syncVersion: mutation.syncVersion + 1,
    completedAt,
  }

  // Optimistically mirror the server's buildings.status cascade so
  // useBuildings() reflects the new status immediately, before the
  // next pull-down sync delivers the authoritative row. Without
  // this, the site-detail building list reads "In progress" for
  // several seconds (or indefinitely if sync is lagging) after the
  // user taps "Mark complete" — looking like the action did nothing.
  //
  // Server cascade (complete-session.ts + reopen-session.ts):
  //   completeSession: flip to 'complete', guarded against the
  //                    admin-only statuses (to_be_demolished /
  //                    out_of_scope) — the freeze means a session
  //                    completion never moves a frozen building.
  //   reopenSession:   guarded flip to 'in_progress' (WHERE status='complete')
  //                    The status='complete' guard mirrors the server's
  //                    own narrowly-scoped recovery branch and prevents
  //                    stomping a building still owned by another
  //                    completed session.
  //
  // Why write to `ps_data__buildings` (the raw underlying table)
  // instead of the `buildings` view: the view has an INSTEAD OF
  // UPDATE trigger that queues a CRUD entry on every write. A
  // `buildings` PATCH would (a) hit the connector's unsupported-op
  // throw and wedge the upload queue, and (b) be redundant — the
  // backend already flipped the row inside the capture_sessions
  // /complete or /reopen transaction. Writing the JSON-encoded
  // `data` column directly updates the local mirror without
  // generating an upload, and the view's `json_extract(data, ...)`
  // SELECT sees the new value on the very next reactive tick.
  //
  // Site-scope sessions (buildingId === null) have no building to
  // flip — skip the write entirely. (Recovery-scenario dispatches that
  // didn't actually transition already returned above, so we never reach
  // here for one.)
  const buildingId = prevBuildingId
  if (typeof buildingId === 'string' && buildingId !== '') {
    if (mutation.kind === 'completeSession') {
      // Status freeze: the NOT IN guard mirrors the server's freeze
      // predicate — a building carrying an admin-only status keeps it.
      await db.execute(
        `UPDATE ps_data__buildings
         SET data = json_set(
           data,
           '$.status', 'complete',
           '$.updated_at', strftime('%Y-%m-%dT%H:%M:%fZ','now')
         )
         WHERE id = ?
           AND json_extract(data, '$.status') NOT IN ('to_be_demolished', 'out_of_scope')`,
        [buildingId],
      )
    } else {
      // reopenSession — guarded by status='complete' (mirrors the
      // server's `where status='complete'` clause). When another
      // path has already flipped the building back to 'in_progress'
      // the json_extract guard makes this a no-op rather than a
      // wrongful overwrite.
      await db.execute(
        `UPDATE ps_data__buildings
         SET data = json_set(
           data,
           '$.status', 'in_progress',
           '$.updated_at', strftime('%Y-%m-%dT%H:%M:%fZ','now')
         )
         WHERE id = ?
           AND json_extract(data, '$.status') = 'complete'`,
        [buildingId],
      )
    }
  }

  const result: CaptureMutationResult = {
    kind: mutation.kind,
    session: row,
  }
  bus.emit(eventForResult(mutation, result))
  return result
}
